import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Key, Plus, Trash2, RefreshCw, Copy, Check, AlertCircle, Eye, Activity } from 'lucide-react';
import { apiKeysAPI } from '../../services/api';
import { useApiKeyPermissions } from '../../hooks/useApiKeyPermissions';


export default function ApiKeysPage() {
  const PERMISSIONS = useApiKeyPermissions();
  const navigate = useNavigate();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    permissions: ['quotes:read', 'quotes:write'],
    rateLimitPerMinute: 60,
    rateLimitPerHour: 1000,
    expiresAt: '',
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);

  // New key display state
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);

  // Delete confirmation state
  const [deleteKeyId, setDeleteKeyId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Rotate confirmation state
  const [rotateKeyId, setRotateKeyId] = useState(null);
  const [rotateLoading, setRotateLoading] = useState(false);

  const fetchApiKeys = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiKeysAPI.list();
      setApiKeys(response.data.apiKeys);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError(null);

    try {
      const response = await apiKeysAPI.create({
        name: createForm.name,
        description: createForm.description || undefined,
        permissions: createForm.permissions,
        rateLimitPerMinute: parseInt(createForm.rateLimitPerMinute),
        rateLimitPerHour: parseInt(createForm.rateLimitPerHour),
        expiresAt: createForm.expiresAt || undefined,
      });

      // Show the new key
      setNewKey(response.data.apiKey);
      setShowCreateModal(false);
      setCreateForm({
        name: '',
        description: '',
        permissions: ['quotes:read', 'quotes:write'],
        rateLimitPerMinute: 60,
        rateLimitPerHour: 1000,
        expiresAt: '',
      });

      // Refresh the list
      fetchApiKeys();
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create API key');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCopyKey = async () => {
    if (newKey?.key) {
      await navigator.clipboard.writeText(newKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDelete = async () => {
    if (!deleteKeyId) return;

    setDeleteLoading(true);
    try {
      await apiKeysAPI.delete(deleteKeyId);
      setDeleteKeyId(null);
      fetchApiKeys();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete API key');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRotate = async () => {
    if (!rotateKeyId) return;

    setRotateLoading(true);
    try {
      const response = await apiKeysAPI.rotate(rotateKeyId);
      setNewKey(response.data.apiKey);
      setRotateKeyId(null);
      fetchApiKeys();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to rotate API key');
    } finally {
      setRotateLoading(false);
    }
  };

  const togglePermission = (permission) => {
    setCreateForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">API Keys</h1>
          <p className="text-neutral-600 dark:text-neutral-400 mt-1">
            Manage API keys for external integrations like QuoteWerks
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Create API Key
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 flex-shrink-0" />
          <p className="text-red-700 dark:text-red-300">{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* New Key Display */}
      {newKey && (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-green-100 dark:bg-green-900/50 rounded-lg">
              <Key className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-green-900 dark:text-green-300">
                {rotateKeyId ? 'API Key Rotated!' : 'API Key Created!'}
              </h3>
              <p className="text-green-700 dark:text-green-400 text-sm mt-1">
                Copy this key now. You won't be able to see it again.
              </p>
              <div className="mt-4 flex items-center gap-2">
                <code className="flex-1 bg-white dark:bg-neutral-800 border border-green-300 dark:border-green-700 rounded px-3 py-2 font-mono text-sm break-all dark:text-green-300">
                  {newKey.key}
                </code>
                <button
                  onClick={handleCopyKey}
                  className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                </button>
              </div>
              <p className="text-green-600 dark:text-green-400 text-xs mt-2">
                Key name: <strong>{newKey.name}</strong> | Prefix: <code>{newKey.keyPrefix}</code>
              </p>
            </div>
            <button
              onClick={() => setNewKey(null)}
              className="text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* API Keys Table */}
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-neutral-200 dark:border-neutral-700">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-neutral-500 dark:text-neutral-400 mt-4">Loading API keys...</p>
          </div>
        ) : apiKeys.length === 0 ? (
          <div className="p-12 text-center">
            <Key className="h-12 w-12 text-neutral-300 dark:text-neutral-600 mx-auto" />
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mt-4">No API Keys</h3>
            <p className="text-neutral-500 dark:text-neutral-400 mt-2">
              Create an API key to enable external integrations.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Create Your First Key
            </button>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
            <thead className="bg-neutral-50 dark:bg-neutral-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Key Prefix
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Last Used
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
              {apiKeys.map((key) => (
                <tr key={key.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-neutral-900 dark:text-white">{key.name}</div>
                      {key.description && (
                        <div className="text-sm text-neutral-500 dark:text-neutral-400 truncate max-w-xs">
                          {key.description}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <code className="text-sm bg-neutral-100 dark:bg-neutral-700 dark:text-neutral-300 px-2 py-1 rounded">
                      {key.keyPrefix}...
                    </code>
                  </td>
                  <td className="px-6 py-4">
                    {key.isActive ? (
                      key.expiresAt && new Date(key.expiresAt) < new Date() ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300">
                          Expired
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300">
                          Active
                        </span>
                      )
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1 text-sm text-neutral-600 dark:text-neutral-300">
                      <Activity className="h-4 w-4" />
                      {key.usageCount.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-neutral-500 dark:text-neutral-400">
                    {key.lastUsedAt ? formatDate(key.lastUsedAt) : 'Never'}
                  </td>
                  <td className="px-6 py-4 text-sm text-neutral-500 dark:text-neutral-400">
                    {formatDate(key.createdAt)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/admin/api-keys/${key.id}`)}
                        className="p-2 text-neutral-500 hover:text-primary-600 hover:bg-primary-50 dark:text-neutral-400 dark:hover:text-primary-400 dark:hover:bg-primary-900/30 rounded-lg"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setRotateKeyId(key.id)}
                        className="p-2 text-neutral-500 hover:text-orange-600 hover:bg-orange-50 dark:text-neutral-400 dark:hover:text-orange-400 dark:hover:bg-orange-900/30 rounded-lg"
                        title="Rotate Key"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteKeyId(key.id)}
                        className="p-2 text-neutral-500 hover:text-red-600 hover:bg-red-50 dark:text-neutral-400 dark:hover:text-red-400 dark:hover:bg-red-900/30 rounded-lg"
                        title="Delete Key"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-neutral-200 dark:border-neutral-700">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Create API Key</h2>
              <p className="text-neutral-500 dark:text-neutral-400 text-sm mt-1">
                Generate a new API key for external integrations
              </p>
            </div>

            <form onSubmit={handleCreateSubmit} className="p-6 space-y-4">
              {createError && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
                  <p className="text-red-700 dark:text-red-300 text-sm">{createError}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder="e.g., QuoteWerks Production"
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Description
                </label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  placeholder="What is this key used for?"
                  rows={2}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Permissions
                </label>
                <div className="space-y-2">
                  {PERMISSIONS.map((perm) => (
                    <label
                      key={perm.value}
                      className="flex items-center gap-3 p-3 border border-neutral-200 dark:border-neutral-600 rounded-lg cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700/50"
                    >
                      <input
                        type="checkbox"
                        checked={createForm.permissions.includes(perm.value)}
                        onChange={() => togglePermission(perm.value)}
                        className="h-4 w-4 text-primary-600 rounded focus:ring-primary-500 dark:bg-neutral-700 dark:border-neutral-600"
                      />
                      <div>
                        <div className="font-medium text-neutral-900 dark:text-white">{perm.label}</div>
                        <div className="text-sm text-neutral-500 dark:text-neutral-400">{perm.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Rate Limit (per minute)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10000"
                    value={createForm.rateLimitPerMinute}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, rateLimitPerMinute: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Rate Limit (per hour)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100000"
                    value={createForm.rateLimitPerHour}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, rateLimitPerHour: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Expiration Date (optional)
                </label>
                <input
                  type="datetime-local"
                  value={createForm.expiresAt}
                  onChange={(e) => setCreateForm({ ...createForm, expiresAt: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-neutral-700 dark:text-white"
                />
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                  Leave empty for no expiration
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createLoading || !createForm.name}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {createLoading ? 'Creating...' : 'Create API Key'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteKeyId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Delete API Key?</h2>
            <p className="text-neutral-600 dark:text-neutral-400 mt-2">
              This action cannot be undone. Any integrations using this key will stop working
              immediately.
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setDeleteKeyId(null)}
                className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteLoading ? 'Deleting...' : 'Delete Key'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rotate Confirmation Modal */}
      {rotateKeyId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Rotate API Key?</h2>
            <p className="text-neutral-600 dark:text-neutral-400 mt-2">
              This will generate a new key and invalidate the old one. You'll need to update any
              integrations using this key.
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setRotateKeyId(null)}
                className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleRotate}
                disabled={rotateLoading}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                {rotateLoading ? 'Rotating...' : 'Rotate Key'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
